import { COURSE_PAGE, HELP_COURSES, courseOf, type HelpCourseDef, type HelpVideoDef, type HelpVideoId } from "./helpVideos.ts";

/**
 * 课程中心的 hash：`#<页面>/<课程 id>/<课 id>`。
 *
 * - 页面是课程中心的某个页签（help-videos、help-learning、help-<学习路径>），路由只认这一段；
 * - 带课程 id 打开那门课的大纲，再带课 id 就在大纲上打开这一节的播放器；
 * - 课程或课 id 认不出来（改名、删课、手误）时两者都给 null，页面回到该页签的首页，不报错。
 *   课 id 存在但不属于这门课程，同样当认不出来。
 *
 * 分享出去的链接一律用 `help-videos` 页签（lessonHash），不跟着当前页签走：
 * 拿到链接的人不一定有同一个页签的上下文。
 */
export interface HelpRoute {
  view: string;
  course: HelpCourseDef | null;
  lesson: HelpVideoDef | null;
  /** hash 里写了课程或课、但认不出来：页面应把地址收回 `#<页面>` */
  invalid: boolean;
}

export function parseHelpHash(hash: string, courses: HelpCourseDef[] = HELP_COURSES): HelpRoute {
  const [view = "", courseId, lessonId] = hash.replace(/^#/, "").split("/");
  const course = courseId ? courses.find((c) => c.id === courseId) ?? null : null;
  if (courseId && !course) return { view, course: null, lesson: null, invalid: true };
  if (!lessonId) return { view, course, lesson: null, invalid: false };
  const lesson = course?.lessons.find((v) => v.id === lessonId) ?? null;
  if (!lesson) return { view, course: null, lesson: null, invalid: true };
  return { view, course, lesson, invalid: false };
}

/** 页内导航用：保留当前页签。不带开头的 # */
export const lessonHashIn = (view: string, lesson: HelpVideoDef) => `${view}/${courseOf(lesson.id).id}/${lesson.id}`;

/** 分享用的单节地址（不带开头的 #），固定在课程中心「全部」页签下 */
export const lessonHash = (id: HelpVideoId) => `${COURSE_PAGE}/${courseOf(id).id}/${id}`;

/**
 * 可分享的完整地址：只拼 origin + pathname + hash。
 *
 * **故意不收整个 location**：地址栏上可能还挂着 super-admin 带来的 `?token=`、`?a0=`
 * （正常会被 bootSession / bootA0 抹掉，但抹之前、或别的查询参数都不该被分享出去）。
 * 只接这两个字段，查询串在构造上就带不进来。
 *
 * 被 wujie 承载时 location 是 wujie 代理过的、指向本应用自己的地址（见 HelpVideo.tsx 的 absolute()），
 * iframe 承载时就是 iframe 自己的 location，两种都拼出本应用的地址，而不是宿主页面的。
 */
export function lessonShareUrl(loc: { origin: string; pathname: string }, id: HelpVideoId): string {
  return `${loc.origin}${loc.pathname}#${lessonHash(id)}`;
}
